import * as Enums from '@/utils/enums';
import {
  ColumnProfile,
  FieldMapping,
  ImportDraft,
  IssueCount,
  ImportRecipe,
  PROBLEM_ERROR,
  PreviewSummary,
  RecipeProblem,
  TargetFieldInfo,
  TransformKind,
} from './import-draft-model';

export const LOW_CONFIDENCE = 0.7;

export type WizardStep = 0 | 1 | 2 | 3;

export function isProcessing(stage: number): boolean {
  return stage === Enums.ImportDraftStage.Uploaded
    || stage === Enums.ImportDraftStage.Parsing
    || stage === Enums.ImportDraftStage.Inferring
    || stage === Enums.ImportDraftStage.Extracting
    || stage === Enums.ImportDraftStage.Committing;
}

export function isExtracted(draft: ImportDraft | null | undefined): boolean {
  return !!draft && draft.Mode === Enums.ImportDraftMode.Extract;
}

export function progressLabel(draft: ImportDraft | null | undefined): string | null {
  if (!draft || draft.Stage !== Enums.ImportDraftStage.Extracting || !draft.ProgressTotal) return null;
  return `${draft.ProgressDone} of ${draft.ProgressTotal} sections processed, ${draft.ProgressRecords.toLocaleString()} records found`;
}

export function progressPercent(draft: ImportDraft | null | undefined): number | null {
  if (!draft || !draft.ProgressTotal) return null;
  return Math.round((draft.ProgressDone / draft.ProgressTotal) * 100);
}

export function stageLabel(stage: number): string {
  switch (stage) {
    case Enums.ImportDraftStage.Uploaded: return 'Waiting to start';
    case Enums.ImportDraftStage.Parsing: return 'Reading the file';
    case Enums.ImportDraftStage.Inferring: return 'Matching columns';
    case Enums.ImportDraftStage.Extracting: return 'Extracting records';
    case Enums.ImportDraftStage.Ready: return 'Ready for review';
    case Enums.ImportDraftStage.Committing: return 'Creating the import';
    case Enums.ImportDraftStage.Committed: return 'Import created';
    case Enums.ImportDraftStage.Failed: return 'Failed';
    case Enums.ImportDraftStage.Cancelled: return 'Cancelled';
    default: return 'Unknown';
  }
}

export function recipeSourceLabel(source: number, sourceLabel?: string | null): string {
  switch (source) {
    case Enums.ImportRecipeSource.Ai: return sourceLabel ? `Matched automatically (${sourceLabel})` : 'Matched automatically';
    case Enums.ImportRecipeSource.Saved: return sourceLabel ? `Saved mapping reused (${sourceLabel})` : 'Saved mapping reused';
    case Enums.ImportRecipeSource.Manual: return 'Edited manually';
    default: return 'Not mapped yet';
  }
}

export function heldBackCount(summary: { HeldBack?: number } | null | undefined): number {
  return summary?.HeldBack ?? 0;
}

export function queueableCount(summary: { TotalRows: number; HeldBack?: number } | null | undefined): number {
  if (!summary) return 0;
  return Math.max(0, summary.TotalRows - heldBackCount(summary));
}

export function softIssueCount(summary: { RowsWithIssues: number; HeldBack?: number } | null | undefined): number {
  if (!summary) return 0;
  return Math.max(0, summary.RowsWithIssues - heldBackCount(summary));
}

export const ATTENTION_CODES = ['ContactNameUnmapped', 'MixedContacts', 'CountryFromNonCountryColumn', 'CustomFieldMapping', 'TypeLooksLikeCategory'];

export const NOTICE_CODES = ['ContactNameUnmapped', 'MixedContacts', 'RetiredTarget', 'TypeLooksLikeCategory'];

export function isNonBlockingCode(code: string | null | undefined): boolean {
  return code === 'RetiredTarget' || code === 'TypeLooksLikeCategory';
}

export function blockingProblems(problems: RecipeProblem[] | null | undefined): RecipeProblem[] {
  return (problems || []).filter(p => p.Severity === PROBLEM_ERROR && !isNonBlockingCode(p.Code));
}

export function isSavedRecipe(draft: ImportDraft | null | undefined): boolean {
  return !!draft && draft.RecipeSource === Enums.ImportRecipeSource.Saved;
}

export function isCommitted(draft: ImportDraft | null | undefined): boolean {
  return !!draft && draft.Stage === Enums.ImportDraftStage.Committed;
}

export function needsAttention(draft: ImportDraft | null | undefined): boolean {
  if (!draft || !draft.Recipe) return true;
  if (hasBlockingProblems(draft.Problems)) return true;
  if ((draft.Problems || []).some(p => !!p.Code && ATTENTION_CODES.includes(p.Code))) return true;
  if (unmappedRequired(draft.Recipe, draft.TargetFields || []).length > 0) return true;
  if (lowConfidenceMappings(draft.Recipe, draft.TargetFields || []).length > 0) return true;
  const rows = Math.max(draft.RowCount || 0, draft.Summary?.TotalRows || 0);
  if (rows <= 0) return true;
  return heldBackCount(draft.Summary) > 0;
}

export function stepForDraft(draft: ImportDraft | null | undefined): WizardStep {
  if (!draft) return 0;
  if (draft.Stage === Enums.ImportDraftStage.Committed) return 3;
  if (draft.Stage !== Enums.ImportDraftStage.Ready) return 0;
  if (heldBackCount(draft.Summary) > 0) return 2;
  const saved = isSavedRecipe(draft);
  if (isExtracted(draft)) return saved && !needsAttention(draft) ? 3 : 2;
  if (!saved) return 1;
  return needsAttention(draft) ? 1 : 3;
}

export interface WizardStepperEntry {
  label: string;
  description: string;
  wizardStep: WizardStep;
}

export function wizardStepper(step: WizardStep, showMapping = step === 1): WizardStepperEntry[] {
  const steps: WizardStepperEntry[] = [
    { label: 'Upload', description: 'Choose a file', wizardStep: 0 },
  ];
  if (showMapping || step === 1) steps.push({ label: 'Mapping', description: 'Match columns', wizardStep: 1 });
  steps.push(
    { label: 'Review', description: 'Check the records', wizardStep: 2 },
    { label: 'Import', description: 'Confirm', wizardStep: 3 },
  );
  return steps;
}

export function rowsForPreviewPage<T>(page: { PageIndex?: number; PageSize?: number; Rows?: T[] } | null | undefined, requestedIndex: number, pageSize: number): T[] {
  const rows = page?.Rows ?? [];
  const size = page?.PageSize && page.PageSize > 0 ? page.PageSize : pageSize;
  if (rows.length > size) return rows.slice(requestedIndex * size, requestedIndex * size + size);
  if (page?.PageIndex !== undefined && page.PageIndex !== requestedIndex) return [];
  return rows;
}

export function pageRangeLabel(pageIndex: number, pageSize: number, totalRows: number, shown: number): string | null {
  if (totalRows <= 0 || shown <= 0) return null;
  const start = pageIndex * pageSize + 1;
  const end = Math.min(totalRows, start + shown - 1);
  return `Rows ${start.toLocaleString()}-${end.toLocaleString()} of ${totalRows.toLocaleString()}`;
}

export function emptyRecipe(): ImportRecipe {
  return { SheetIndex: 0, HeaderRowIndex: 0, DataStartRowIndex: 1, Fields: [], Ignored: [], ValueMaps: [], Warnings: [] };
}

export function cloneRecipe(recipe: ImportRecipe): ImportRecipe {
  return JSON.parse(JSON.stringify(recipe));
}

export function findMapping(recipe: ImportRecipe, target: string): FieldMapping | undefined {
  return recipe.Fields.find(f => f.Target.toLowerCase() === target.toLowerCase());
}

export function setFieldSource(recipe: ImportRecipe, target: string, columnIndex: number | null): ImportRecipe {
  const next = cloneRecipe(recipe);
  const existing = next.Fields.find(f => f.Target.toLowerCase() === target.toLowerCase());

  if (columnIndex === null) {
    next.Fields = next.Fields.filter(f => f !== existing);
    return recompute(next);
  }

  if (existing) {
    existing.Sources = [columnIndex];
    existing.Confidence = 1;
    existing.Reason = 'Selected manually';
    if (existing.Transform === 'Constant') existing.Transform = 'Copy';
  } else {
    next.Fields.push({ Target: target, Sources: [columnIndex], Transform: 'Copy', Confidence: 1, Reason: 'Selected manually' });
  }
  return recompute(next);
}

export function setFieldTransform(recipe: ImportRecipe, target: string, transform: TransformKind, argument?: string | null): ImportRecipe {
  const next = cloneRecipe(recipe);
  const existing = next.Fields.find(f => f.Target.toLowerCase() === target.toLowerCase());
  if (!existing) return next;
  if (existing.Transform === 'AiClean' || transform === 'AiClean') dropValueMap(next, target);
  existing.Transform = transform;
  existing.Argument = argument ?? null;
  existing.Confidence = 1;
  return recompute(next);
}

export function setFieldArgument(recipe: ImportRecipe, target: string, argument: string): ImportRecipe {
  const next = cloneRecipe(recipe);
  const existing = next.Fields.find(f => f.Target.toLowerCase() === target.toLowerCase());
  if (!existing) return next;
  if (existing.Transform === 'AiClean' && (existing.Argument || '') !== argument) dropValueMap(next, target);
  existing.Argument = argument.length ? argument : null;
  return next;
}

export function dropValueMap(recipe: ImportRecipe, target: string): ImportRecipe {
  recipe.ValueMaps = recipe.ValueMaps.filter(v => v.Target.toLowerCase() !== target.toLowerCase());
  return recipe;
}

export function cleaningExamples(recipe: ImportRecipe, target: string, max = 3): { From: string; To: string }[] {
  const map = recipe.ValueMaps.find(v => v.Target.toLowerCase() === target.toLowerCase());
  return (map?.Entries || []).slice(0, max);
}

export function cleaningCount(recipe: ImportRecipe, target: string): number | null {
  const map = recipe.ValueMaps.find(v => v.Target.toLowerCase() === target.toLowerCase());
  return map ? map.Entries.length : null;
}

export function setValueMapEntry(recipe: ImportRecipe, target: string, from: string, to: string): ImportRecipe {
  const next = cloneRecipe(recipe);
  let map = next.ValueMaps.find(v => v.Target.toLowerCase() === target.toLowerCase());
  if (!map) {
    map = { Target: target, Entries: [] };
    next.ValueMaps.push(map);
  }
  const entry = map.Entries.find(e => e.From === from);
  if (entry) entry.To = to;
  else map.Entries.push({ From: from, To: to });
  return next;
}

export function recompute(recipe: ImportRecipe): ImportRecipe {
  const used = new Set(recipe.Fields.flatMap(f => f.Sources));
  recipe.Ignored = recipe.Ignored.filter(i => !used.has(i.ColumnIndex));
  return recipe;
}

export function unmappedRequired(recipe: ImportRecipe | null | undefined, fields: TargetFieldInfo[]): TargetFieldInfo[] {
  if (!recipe) return fields.filter(f => f.Required);
  return fields.filter(f => f.Required && !findMapping(recipe, f.Name));
}

export function lowConfidenceMappings(recipe: ImportRecipe | null | undefined, fields?: TargetFieldInfo[]): FieldMapping[] {
  const shaky = (recipe?.Fields || []).filter(f => f.Confidence < LOW_CONFIDENCE);
  if (!fields) return shaky;
  const required = new Set(fields.filter(f => f.Required).map(f => f.Name.toLowerCase()));
  return shaky.filter(f => required.has(f.Target.toLowerCase()));
}

export function unmappedColumns(recipe: ImportRecipe | null | undefined, profiles: ColumnProfile[]): ColumnProfile[] {
  const used = new Set((recipe?.Fields || []).flatMap(f => f.Sources));
  return profiles.filter(p => !used.has(p.Index) && (p.Header || p.NonEmptyCount > 0));
}

export const DATA_COLUMN_FILL = 0.2;

export function columnFillRatio(profile: ColumnProfile): number {
  if (profile.RowCount > 0) return profile.NonEmptyCount / profile.RowCount;
  return profile.FillRatio || 0;
}

export function ignoredColumnsWithData(recipe: ImportRecipe | null | undefined, profiles: ColumnProfile[]): ColumnProfile[] {
  return unmappedColumns(recipe, profiles).filter(p => columnFillRatio(p) > DATA_COLUMN_FILL);
}

export function ignoredReason(recipe: ImportRecipe | null | undefined, columnIndex: number): string | null {
  return recipe?.Ignored.find(i => i.ColumnIndex === columnIndex)?.Reason || null;
}

export function columnLabel(profiles: ColumnProfile[], index: number): string {
  const profile = profiles.find(p => p.Index === index);
  const header = profile?.Header?.trim();
  return header ? `${header}` : `Column ${index + 1}`;
}

export function columnSamples(profiles: ColumnProfile[], index: number, max = 3): string[] {
  const profile = profiles.find(p => p.Index === index);
  return (profile?.Samples || []).slice(0, max);
}

export function problemsWithCode(problems: RecipeProblem[] | null | undefined, code: string): RecipeProblem[] {
  return (problems || []).filter(p => p.Code === code);
}

export function problemsForTarget(problems: RecipeProblem[] | null | undefined, target: string): RecipeProblem[] {
  return (problems || []).filter(p => p.Severity !== PROBLEM_ERROR && !!p.Target && p.Target.toLowerCase() === target.toLowerCase() && !(p.Code && NOTICE_CODES.includes(p.Code)));
}

const CUSTOM_TARGET = /^Custom(?!er)[A-Za-z]*\d+$/i;

export function isCustomFieldTarget(target: string | null | undefined): boolean {
  return !!target && CUSTOM_TARGET.test(target);
}

export function canIgnoreProblem(problem: RecipeProblem, target: string): boolean {
  if (problem.Code === 'CountryFromNonCountryColumn') return true;
  return problem.Code === 'CustomFieldMapping' && isCustomFieldTarget(target);
}

export interface CategoryMove {
  problem: RecipeProblem;
  columnIndex: number | null;
}

export function categoryMoves(problems: RecipeProblem[] | null | undefined, recipe: ImportRecipe | null | undefined): CategoryMove[] {
  return problemsWithCode(problems, 'TypeLooksLikeCategory').map(problem => {
    const fromProblem = typeof problem.ColumnIndex === 'number' ? problem.ColumnIndex : null;
    const fromCategory = recipe ? findMapping(recipe, 'Category')?.Sources?.[0] ?? null : null;
    const fromType = recipe ? findMapping(recipe, 'Type')?.Sources?.[0] ?? null : null;
    return { problem, columnIndex: fromProblem ?? fromCategory ?? fromType };
  });
}

export function typeRestored(recipe: ImportRecipe | null | undefined, columnIndex: number | null): boolean {
  if (!recipe || columnIndex === null) return false;
  return !!findMapping(recipe, 'Type')?.Sources.includes(columnIndex);
}

export function mapBackToType(recipe: ImportRecipe, columnIndex: number): ImportRecipe {
  const category = findMapping(recipe, 'Category');
  const next = category && category.Sources.includes(columnIndex) ? setFieldSource(recipe, 'Category', null) : recipe;
  return setFieldSource(next, 'Type', columnIndex);
}

export function generalWarnings(problems: RecipeProblem[] | null | undefined): RecipeProblem[] {
  return (problems || []).filter(p => p.Severity !== PROBLEM_ERROR && !p.Target && !p.Code);
}

export const CONTACT_NAME_FIELDS = ['FullName', 'FirstName', 'LastName'];

export function contactNameMapped(recipe: ImportRecipe | null | undefined): boolean {
  if (!recipe) return false;
  return CONTACT_NAME_FIELDS.some(name => !!findMapping(recipe, name));
}

export function suggestedContactColumn(problem: RecipeProblem | null | undefined, profiles: ColumnProfile[]): number | null {
  if (!problem) return null;
  if (typeof problem.ColumnIndex === 'number' && profiles.some(p => p.Index === problem.ColumnIndex)) return problem.ColumnIndex;
  const message = (problem.Message || '').toLowerCase();
  const named = profiles
    .filter(p => (p.Header || '').trim().length >= 2)
    .map(p => ({ index: p.Index, header: p.Header.trim().toLowerCase() }));
  const quoted = Array.from(message.matchAll(/["'\u201c\u2018]([^"'\u201d\u2019]+)["'\u201d\u2019]/g)).map(m => m[1].trim());
  const exact = named.find(n => quoted.includes(n.header));
  if (exact) return exact.index;
  const contained = named.filter(n => message.includes(n.header)).sort((a, b) => b.header.length - a.header.length);
  return contained.length ? contained[0].index : null;
}

export function hasTargetField(fields: TargetFieldInfo[] | null | undefined, name: string): boolean {
  return (fields || []).some(f => f.Name.toLowerCase() === name.toLowerCase());
}

export function mapContactName(recipe: ImportRecipe, fields: TargetFieldInfo[], columnIndex: number): ImportRecipe {
  if (hasTargetField(fields, 'FullName')) return setFieldSource(recipe, 'FullName', columnIndex);
  let next = setFieldSource(recipe, 'FirstName', columnIndex);
  next = setFieldTransform(next, 'FirstName', 'SplitFirst', null);
  next = setFieldSource(next, 'LastName', columnIndex);
  next = setFieldTransform(next, 'LastName', 'SplitRest', null);
  return next;
}

export interface FieldSummaryRow {
  target: string;
  source: string;
  sample: string | null;
  transform: TransformKind;
  argument?: string | null;
}

export function fieldSummaryRows(recipe: ImportRecipe | null | undefined, profiles: ColumnProfile[]): FieldSummaryRow[] {
  return (recipe?.Fields || []).map(field => {
    const constant = field.Transform === 'Constant';
    const source = constant
      ? 'Fixed value'
      : field.Sources.length ? field.Sources.map(index => columnLabel(profiles, index)).join(' + ') : 'Generated';
    const sample = constant
      ? field.Argument || null
      : field.Sources.length ? columnSamples(profiles, field.Sources[0], 1)[0] ?? null : null;
    return { target: field.Target, source, sample, transform: field.Transform, argument: field.Argument };
  });
}

export function importFailureSummary(draft: ImportDraft | null | undefined): string | null {
  return draft?.ImportFailureExplanation || draft?.Summary?.ImportFailureExplanation || null;
}

export function leadingZeroCounts(summary: PreviewSummary | null | undefined): { restored: number; uncertain: number } {
  const restored = typeof summary?.LeadingZerosRestored === 'number' ? summary.LeadingZerosRestored : 0;
  const uncertainValue = summary?.LeadingZerosUncertain ?? summary?.LeadingZeroUncertain;
  const uncertain = typeof uncertainValue === 'number' ? uncertainValue : 0;
  return { restored, uncertain };
}

export function hasBlockingProblems(problems: RecipeProblem[] | null | undefined): boolean {
  return blockingProblems(problems).length > 0;
}

export function softIssues(summary: PreviewSummary | null | undefined): IssueCount[] {
  if (!summary) return [];
  const blocking = new Map<string, number>();
  (summary.BlockingIssues || []).forEach(i => blocking.set(`${i.Target}\u0000${i.Message}`, (blocking.get(`${i.Target}\u0000${i.Message}`) || 0) + i.Count));
  return (summary.IssuesByField || [])
    .map(i => ({ ...i, Count: i.Count - (blocking.get(`${i.Target}\u0000${i.Message}`) || 0) }))
    .filter(i => i.Count > 0);
}

export function heldBackIssues(summary: PreviewSummary | null | undefined): IssueCount[] {
  if (!summary) return [];
  return summary.BlockingIssues ?? summary.IssuesByField ?? [];
}

export type ImportOutcomeState = 'queued' | 'completed' | 'errors';

export function importOutcomeState(summary: PreviewSummary | null | undefined): ImportOutcomeState {
  const outcome = (summary?.ImportOutcome || '').trim().toLowerCase();
  if (!outcome) return 'queued';
  return outcome === 'completed' ? 'completed' : 'errors';
}

export function canContinueFromMapping(draft: ImportDraft | null | undefined, recipe: ImportRecipe | null | undefined): boolean {
  if (!draft || !recipe) return false;
  if (draft.Stage !== Enums.ImportDraftStage.Ready) return false;
  return unmappedRequired(recipe, draft.TargetFields).length === 0;
}

export function confidenceTone(confidence: number): 'green' | 'yellow' | 'red' {
  if (confidence >= 0.9) return 'green';
  if (confidence >= LOW_CONFIDENCE) return 'yellow';
  return 'red';
}

export function formatTokens(draft: ImportDraft | null | undefined): string | null {
  if (!draft || (!draft.InputTokens && !draft.OutputTokens)) return null;
  const total = draft.InputTokens + draft.OutputTokens;
  const runs = draft.InferenceCount > 1 ? ` over ${draft.InferenceCount} runs` : '';
  return `${total.toLocaleString()} tokens${runs}`;
}

export function importTypeName(importType: number): string {
  const entry = Object.entries(Enums.ImportType).find(([, value]) => value === importType);
  return entry ? entry[0] : 'Import';
}

export const SMART_IMPORT_TYPES = [Enums.ImportType.Customer, Enums.ImportType.Inventory];

export function acceptedFile(file: File | null | undefined): string | null {
  if (!file) return 'Choose a file first.';
  const name = file.name.toLowerCase();
  const ok = ['.csv', '.txt', '.tsv', '.xlsx', '.xlsm', '.xls', '.pdf'].some(ext => name.endsWith(ext));
  if (!ok) return 'Only CSV, Excel and text PDF files are supported.';
  if (file.size > 25 * 1024 * 1024) return 'Files larger than 25 MB are not supported. Split the file into smaller files and try again.';
  return null;
}

export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export function formatDuration(ms: number | null | undefined): string {
  if (!ms || ms <= 0) return '-';
  const seconds = Math.round(ms / 1000);
  if (seconds < 60) return `${Math.max(1, seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const rest = seconds % 60;
  return rest ? `${minutes}m ${rest}s` : `${minutes}m`;
}

export function modeLabel(draft: ImportDraft): string {
  if (draft.Mode !== Enums.ImportDraftMode.Extract) return 'Table';
  return draft.RecipeSource === Enums.ImportRecipeSource.Saved ? 'Report (saved rules)' : 'Report';
}

export type RunStatusFilter = 'all' | 'active' | 'ready' | 'imported' | 'failed';

export function runStatusGroup(stage: number): Exclude<RunStatusFilter, 'all'> | 'cancelled' {
  if (isProcessing(stage)) return 'active';
  if (stage === Enums.ImportDraftStage.Ready) return 'ready';
  if (stage === Enums.ImportDraftStage.Committed) return 'imported';
  if (stage === Enums.ImportDraftStage.Failed) return 'failed';
  return 'cancelled';
}

export function stageColour(stage: number): string {
  switch (runStatusGroup(stage)) {
    case 'active': return 'blue';
    case 'ready': return 'yellow';
    case 'imported': return 'green';
    case 'failed': return 'red';
    default: return 'gray';
  }
}

export interface RunTotals {
  runs: number;
  active: number;
  imported: number;
  failed: number;
  recordsImported: number;
  medianDurationMs: number | null;
}

export function runTotals(drafts: ImportDraft[]): RunTotals {
  const durations = drafts.map(d => d.DurationMs).filter(ms => ms > 0).sort((a, b) => a - b);
  const imported = drafts.filter(d => d.Stage === Enums.ImportDraftStage.Committed);
  return {
    runs: drafts.length,
    active: drafts.filter(d => isProcessing(d.Stage)).length,
    imported: imported.length,
    failed: drafts.filter(d => d.Stage === Enums.ImportDraftStage.Failed).length,
    recordsImported: imported.reduce((sum, d) => sum + d.RowCount, 0),
    medianDurationMs: durations.length ? durations[Math.floor(durations.length / 2)] : null,
  };
}
